import { useState, useRef, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { parseICSContent, fetchAndParseCalendar, storeCalendarInLocalStorage } from '@/lib/calendarParser'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import { toast } from 'sonner'

/**
 * CalendarUpload Component
 * 
 * Three modes:
 * - Mode A (Public): Temporary upload to localStorage for instant filtering
 * - Mode B (Settings): Permanent upload to database for logged-in users
 * - Mode C (Inline): Compact inline mode for scrimmage board with drag-and-drop
 * 
 * Features:
 * - Tab interface: URL vs File Upload
 * - Drag-and-drop support for .ics files
 * - Client-side parsing (public) or Edge Function (authenticated)
 * - Preview events before applying
 * - Match preview for inline mode
 * - Help guides for finding calendar URLs
 * - Sign-up CTA for public users
 */

export default function CalendarUpload({ 
  mode = 'public', // 'public', 'settings', or 'inline'
  teamId = null,
  onCalendarUploaded = null,
  onCancel = null,
  compact = false,
  matchCount = null // For showing "Y potential matches" in inline mode
}) {
  const [tab, setTab] = useState('url')
  const [calendarUrl, setCalendarUrl] = useState('')
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [showHelp, setShowHelp] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)

  const isPublic = mode === 'public' || mode === 'inline'
  const isSettings = mode === 'settings'
  const isInline = mode === 'inline'

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const droppedFile = files[0]
      if (droppedFile.name.endsWith('.ics') || droppedFile.name.endsWith('.xml')) {
        processFile(droppedFile)
      } else {
        toast.error('Please drop an .ics calendar file')
      }
    }
  }, [])

  const processFile = async (selectedFile) => {
    setFile(selectedFile)
    setLoading(true)
    setPreview(null)
    setTab('file')

    try {
      const reader = new FileReader()
      
      reader.onload = async (event) => {
        const icsData = event.target.result

        if (isPublic) {
          const result = parseICSContent(icsData)
          
          if (!result.success) {
            toast.error(result.error)
            setLoading(false)
            return
          }

          setPreview({
            ...result,
            fileName: selectedFile.name,
            calendarType: 'ics_upload'
          })

          if (result.warning) {
            toast.warning(result.warning)
          }
        } else {
          // Settings mode handling stays the same
          if (!teamId) {
            toast.error('Team ID is required')
            setLoading(false)
            return
          }

          const { data: { session } } = await supabase.auth.getSession()
          
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-team-calendar`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
            },
            body: JSON.stringify({
              teamId,
              calendarContent: icsData,
              calendarType: 'ics_upload',
              calendarName: selectedFile.name
            })
          })

          const data = await response.json()

          if (!response.ok) {
            toast.error(data.error || 'Failed to upload calendar')
            setLoading(false)
            return
          }

          toast.success(`Calendar uploaded! ${data.eventsCount} events found.`)
          
          if (data.warning) {
            toast.warning(data.warning)
          }

          if (onCalendarUploaded) {
            onCalendarUploaded(data)
          }
        }

        setLoading(false)
      }

      reader.onerror = () => {
        toast.error('Failed to read file')
        setLoading(false)
      }

      reader.readAsText(selectedFile)

    } catch (error) {
      console.error('[CalendarUpload] Error:', error)
      toast.error('Failed to upload calendar. Please try again.')
      setLoading(false)
    }
  }

  const handleUrlUpload = async () => {
    if (!calendarUrl.trim()) {
      toast.error('Please enter a calendar URL')
      return
    }

    setLoading(true)
    setPreview(null)

    try {
      if (isPublic) {
        // Public mode: Parse client-side
        const normalizedUrl = calendarUrl.replace(/^webcal:\/\//i, 'https://')
        const result = await fetchAndParseCalendar(normalizedUrl)
        
        if (!result.success) {
          toast.error(result.error)
          return
        }

        setPreview({
          ...result,
          calendarUrl: normalizedUrl,
          calendarType: 'webcal'
        })

        if (result.warning) {
          toast.warning(result.warning)
        }

      } else {
        // Settings mode: Send to Edge Function
        if (!teamId) {
          toast.error('Team ID is required')
          return
        }

        const { data: { session } } = await supabase.auth.getSession()
        
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-team-calendar`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            teamId,
            calendarUrl,
            calendarType: 'webcal',
            calendarName: 'Webcal Feed'
          })
        })

        const data = await response.json()

        if (!response.ok) {
          toast.error(data.error || 'Failed to upload calendar')
          return
        }

        toast.success(`Calendar uploaded! ${data.eventsCount} events found.`)
        
        if (data.warning) {
          toast.warning(data.warning)
        }

        if (onCalendarUploaded) {
          onCalendarUploaded(data)
        }
      }

    } catch (error) {
      console.error('[CalendarUpload] Error:', error)
      toast.error('Failed to upload calendar. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    // Allow both .ics and .xml (RSS) files
    if (!selectedFile.name.endsWith('.ics') && !selectedFile.name.endsWith('.xml')) {
      toast.error('Please select an .ics calendar file or .xml RSS feed')
      return
    }

    processFile(selectedFile)
  }

  const handleApplyToFilters = () => {
    if (!preview || !preview.events) {
      toast.error('No calendar to apply')
      return
    }

    // Store in localStorage
    const result = storeCalendarInLocalStorage(preview.events, preview.fileName || 'Uploaded Calendar')
    
    if (result === true) {
      toast.success('Calendar applied to filters!')
      
      if (onCalendarUploaded) {
        onCalendarUploaded(preview)
      }
    } else if (result.error) {
      toast.error(result.error)
    }
  }

  // Inline compact mode render
  if (isInline) {
    return (
      <div 
        className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
          isDragging 
            ? 'border-brand-green bg-green-50' 
            : 'border-gray-300 bg-gray-50 hover:border-brand-green hover:bg-green-50/50'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {!preview ? (
          <div className="text-center">
            <div className="mb-3">
              <svg className="w-10 h-10 mx-auto text-brand-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-brand-charcoal mb-1">
              {isDragging ? 'Drop your calendar here!' : 'Upload your calendar for personalized matches'}
            </p>
            <p className="text-xs text-gray-500 mb-3">
              Drag & drop .ics file or paste your SportsEngine/TeamSnap URL
            </p>
            
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".ics,.xml"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
              >
                Browse Files
              </Button>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={calendarUrl}
                  onChange={(e) => setCalendarUrl(e.target.value)}
                  placeholder="Paste URL here"
                  className="input text-sm py-1.5 px-3 w-48"
                  disabled={loading}
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUrlUpload}
                  disabled={loading || !calendarUrl.trim()}
                  loading={loading}
                >
                  Go
                </Button>
              </div>
            </div>

            <button
              onClick={() => setShowHelp(!showHelp)}
              className="text-xs text-brand-green hover:underline mt-3"
            >
              Where do I find my calendar URL?
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm mb-3">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Found {preview.eventsCount} events
              {preview.futureEventsCount > 0 && ` (${preview.futureEventsCount} upcoming)`}
            </div>
            
            {matchCount !== null && (
              <p className="text-sm font-medium text-brand-charcoal mb-3">
                Based on your schedule, there are <span className="text-brand-green font-bold">{matchCount}</span> potential matches
              </p>
            )}
            
            <div className="flex gap-2 justify-center">
              <Button
                variant="primary"
                size="sm"
                onClick={handleApplyToFilters}
              >
                Apply to Filters
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPreview(null)
                  setFile(null)
                  setCalendarUrl('')
                }}
              >
                Try Different Calendar
              </Button>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              <a href="/auth?tab=signup" className="text-brand-green hover:underline">
                Create a free account
              </a>
              {' '}to save your calendar and get alerts
            </p>
          </div>
        )}

        {showHelp && (
          <div className="mt-4 pt-4 border-t border-gray-200 text-left">
            <p className="text-xs text-gray-600 mb-2"><strong>Quick guide:</strong></p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• <strong>SportsEngine:</strong> Team page → Calendar → Export → Copy URL</li>
              <li>• <strong>TeamSnap:</strong> Calendar → Export icon → Copy webcal link</li>
              <li>• <strong>Google Calendar:</strong> Settings → Integrate → Secret iCal address</li>
            </ul>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Drag and Drop Zone */}
      <div 
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging 
            ? 'border-brand-green bg-green-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="mb-2">
          <svg className="w-10 h-10 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        </div>
        <p className="text-sm text-gray-600 mb-1">
          {isDragging ? 'Drop your .ics file here!' : 'Drag and drop your .ics calendar file here'}
        </p>
        <p className="text-xs text-gray-500">or use the tabs below</p>
      </div>

      {/* Tab Selector */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setTab('url')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            tab === 'url'
              ? 'border-brand-green text-brand-green'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Calendar URL
        </button>
        <button
          onClick={() => setTab('file')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            tab === 'file'
              ? 'border-brand-green text-brand-green'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          File Upload
        </button>
      </div>

      {/* URL Upload Tab */}
      {tab === 'url' && (
        <div className="space-y-4">
          <div>
            <label className="label">Calendar URL (webcal, https, or RSS)</label>
            <input
              type="text"
              value={calendarUrl}
              onChange={(e) => setCalendarUrl(e.target.value)}
              placeholder="webcal://example.com/calendar.ics or https://example.com/rss"
              className="input"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter your calendar's webcal, HTTPS, or RSS feed URL
            </p>
          </div>

          <Button
            variant="primary"
            onClick={handleUrlUpload}
            disabled={loading || !calendarUrl.trim()}
            loading={loading}
          >
            {isPublic ? 'Parse Calendar' : 'Upload Calendar'}
          </Button>
        </div>
      )}

      {/* File Upload Tab */}
      {tab === 'file' && (
        <div className="space-y-4">
          <div>
            <label className="label">Calendar File (.ics or .xml RSS)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".ics,.xml"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded file:border-0
                file:text-sm file:font-semibold
                file:bg-brand-green file:text-white
                hover:file:bg-green-700
                file:cursor-pointer cursor-pointer"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Select an .ics calendar file or .xml RSS feed from your computer
            </p>
          </div>

          {file && (
            <p className="text-sm text-gray-700">
              Selected: <strong>{file.name}</strong>
            </p>
          )}
        </div>
      )}

      {/* Preview */}
      {preview && (
        <Card className="bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-brand-charcoal mb-2">
            ✓ Calendar Preview
          </h3>
          <div className="text-sm text-gray-700 space-y-1">
            <p>
              <strong>Events found:</strong> {preview.eventsCount}
            </p>
            <p>
              <strong>Future events:</strong> {preview.futureEventsCount}
            </p>
            {preview.dateRange?.earliest && (
              <p>
                <strong>Date range:</strong>{' '}
                {new Date(preview.dateRange.earliest).toLocaleDateString()} to{' '}
                {new Date(preview.dateRange.latest).toLocaleDateString()}
              </p>
            )}
          </div>

          {isPublic && (
            <div className="mt-4">
              <Button
                variant="primary"
                onClick={handleApplyToFilters}
                className="w-full"
              >
                Apply to Filters
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Sign-up CTA for public mode */}
      {isPublic && preview && (
        <Card className="bg-green-50 border-green-200">
          <p className="text-sm text-gray-700 mb-3">
            <strong>Want to save your calendar and get email alerts?</strong>
          </p>
          <Button
            variant="primary"
            onClick={() => window.location.href = '/auth?tab=signup'}
            className="w-full"
          >
            Sign Up for Free
          </Button>
        </Card>
      )}

      {/* Help Accordion */}
      <div>
        <button
          onClick={() => setShowHelp(!showHelp)}
          className="text-sm text-brand-green hover:underline font-medium"
        >
          {showHelp ? '− Hide' : '+ Show'} How to find your calendar URL
        </button>

        {showHelp && (
          <Card className="mt-2 bg-gray-50">
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-semibold text-brand-charcoal mb-1">TeamSnap</h4>
                <ol className="list-decimal list-inside text-gray-700 space-y-1">
                  <li>Go to your team page</li>
                  <li>Click "Calendar" in the navigation</li>
                  <li>Click the export icon (📥) or "Export Calendar"</li>
                  <li>Copy the webcal URL</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold text-brand-charcoal mb-1">SportsEngine</h4>
                <ol className="list-decimal list-inside text-gray-700 space-y-1">
                  <li>Log in to SportsEngine</li>
                  <li>Go to Settings or Calendar Settings</li>
                  <li>Look for "Export Calendar" or "iCal Feed"</li>
                  <li>Copy the webcal or HTTPS URL</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold text-brand-charcoal mb-1">Google Calendar</h4>
                <ol className="list-decimal list-inside text-gray-700 space-y-1">
                  <li>Open Google Calendar</li>
                  <li>Click the calendar settings (gear icon)</li>
                  <li>Click "Settings" then select your calendar</li>
                  <li>Scroll to "Integrate calendar"</li>
                  <li>Copy the "Secret address in iCal format"</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold text-brand-charcoal mb-1">Outlook/Office 365</h4>
                <ol className="list-decimal list-inside text-gray-700 space-y-1">
                  <li>Open Outlook Calendar</li>
                  <li>Right-click on your calendar name</li>
                  <li>Select "Publish" or "Sharing"</li>
                  <li>Copy the ICS or webcal URL</li>
                </ol>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Cancel button for modal */}
      {onCancel && (
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}

